import React, { useId, useState } from 'react';
import { calcRecharge, calcSale, calcWithdraw, formatCents } from '../../utils/marketMath';
import { examplePercents } from '../../data/market/config';
const input = 'h-11 w-full rounded-xl border border-white/15 bg-[#050b14] px-3 text-base text-white placeholder:text-gray-600 focus:border-[#D4AF37]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/40';
function Field({
  label,
  value,
  onChange,
  suffix,
  mode = 'decimal'
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  suffix: string;
  mode?: 'decimal' | 'numeric';
}) {
  const id = useId();
  return <div data-id="element-984" data-mp="wteyuxotupfx">
      <label htmlFor={id} className="mb-1 block text-sm text-gray-300" data-id="element-985" data-mp="wt5837vyke96">
        {label}
      </label>
      <div className="relative" data-id="element-986" data-mp="su60tgmr85ek">
        <input id={id} value={value} onChange={e => onChange(e.target.value)} inputMode={mode} autoComplete="off" className={`${input} pr-16`} data-id="element-987" data-mp="sr0g0dl5rpon" />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500" data-id="element-988" data-mp="2ul9gwlfeyc6i">{suffix}</span>
      </div>
    </div>;
}
function Out({
  rows,
  error,
  warning
}: {
  rows?: [string, string, boolean?][];
  error?: string;
  warning?: string;
}) {
  return <div aria-live="polite" className="mt-4 border-t border-white/10 pt-4" data-id="element-989" data-mp="1nk9jgft6x43n">
      {error ? <p className="text-sm text-amber-200" data-id="element-990" data-mp="1sc00f4mmuq41">{error}</p> : <dl className="space-y-1.5" data-id="element-991" data-mp="1sdb3t3c61vum">
          {rows?.map(([k, v, strong]) => <div key={k} className="flex items-baseline justify-between gap-3" data-id="element-992" data-mp="1xabbtjskt8od">
              <dt className="text-sm text-gray-400" data-id="element-993" data-mp="21av8ynd5gcai">{k}</dt>
              <dd className={strong ? 'font-cinzel text-2xl font-bold text-[#D4AF37]' : 'text-sm font-semibold text-white'} data-id="element-994" data-mp="26bixn2dwoh1k">{v}</dd>
            </div>)}
        </dl>}
      {warning && <p className="mt-2 text-sm text-amber-200" data-id="element-995" data-mp="bxhwgauvvhyb">{warning}</p>}
    </div>;
}

/** Tres calculadoras educativas. No consultan el saldo ni ejecutan operaciones. */
export function MarketCalculators() {
  const [usdt, setUsdt] = useState('12,50');
  const [price, setPrice] = useState('101');
  const [salePct, setSalePct] = useState(examplePercents.sale);
  const [credits, setCredits] = useState('101');
  const [wPct, setWPct] = useState(examplePercents.withdraw);
  const r = calcRecharge(usdt);
  const s = calcSale(price, salePct);
  const w = calcWithdraw(credits, wPct);
  return <section id="calculadoras-interactivas" aria-labelledby="calc-t" className="scroll-mt-32" data-id="element-996" data-mp="dx2rfrg5u7k8">
      <h2 id="calc-t" className="font-cinzel text-lg font-bold text-white md:text-xl" data-id="element-997" data-mp="rs4um6tmhitc">
        Calculadoras
      </h2>
      <p className="mt-2 rounded-2xl border border-white/10 px-4 py-3 text-sm text-gray-300" data-id="element-998" data-mp="11k17io0dbdhm">
        Calculadora educativa. No consulta tu saldo ni ejecuta operaciones. Confirma siempre los importes y condiciones en el Market.
      </p>
      <div className="mt-4 grid gap-4 lg:grid-cols-3" data-id="element-999" data-mp="1bj9xl5s447yp">
        <div className="rounded-2xl border border-white/10 bg-[#0A1628] p-4" data-id="element-1000" data-mp="16jdpaucdtudc">
          <h3 className="font-semibold text-white" data-id="element-1001" data-mp="16havdz83zh3c">Donación</h3>
          <p className="mb-3 text-xs text-gray-500" data-id="element-1002" data-mp="1q6ie8m46x5i2">USDT × 10, en múltiplos de 0,10.</p>
          <Field label="Importe a donar" value={usdt} onChange={setUsdt} suffix="USDT" data-id="element-1003" data-mp="1u5r61lfo6taz" />
          {r.ok ? <Out rows={[['Créditos previstos', String(r.credits), true]]} data-id="element-1004" data-mp="1g5y7j3xwj8sg" /> : <Out error={r.error} data-id="element-1005" data-mp="1l642ou3drdfa" />}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0A1628] p-4" data-id="element-1006" data-mp="1l4sy4s2421n4">
          <h3 className="font-semibold text-white" data-id="element-1007" data-mp="16gy4n9ga3t8z">Venta</h3>
          <p className="mb-3 text-xs text-gray-500" data-id="element-1008" data-mp="16fdf5y8tn0hz">Comisión redondeada hacia arriba al crédito entero.</p>
          <div className="space-y-3" data-id="element-1009" data-mp="xgi86jxydmdm">
            <Field label="Precio de venta" value={price} onChange={setPrice} suffix="créditos" mode="numeric" data-id="element-1010" data-mp="xhkusfo8t2nq" />
            <Field label="Porcentaje de ejemplo" value={salePct} onChange={setSalePct} suffix="%" data-id="element-1011" data-mp="12ge7w6zmarnd" />
          </div>
          {s.ok ? <Out rows={[['Comisión', String(s.fee)], ['Neto para el vendedor', String(Math.max(s.net, 0)), true]]} warning={s.consumed ? 'El redondeo de la comisión consume todo el precio: no recibirías créditos.' : undefined} data-id="element-1012" data-mp="nkoma9ycggj6" /> : <Out error={s.error} data-id="element-1013" data-mp="1quat072w16mr" />}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0A1628] p-4" data-id="element-1014" data-mp="1m2jbem1pmb8r">
          <h3 className="font-semibold text-white" data-id="element-1015" data-mp="1m0gb996uf1qb">Retiro</h3>
          <p className="mb-3 text-xs text-gray-500" data-id="element-1016" data-mp="1h2ex92o9xslp">Mínimo 100 créditos; comisión redondeada hacia arriba al centavo.</p>
          <div className="space-y-3" data-id="element-1017" data-mp="1c5wprv1k7g5o">
            <Field label="Créditos a retirar" value={credits} onChange={setCredits} suffix="créditos" mode="numeric" data-id="element-1018" data-mp="182rv2enq0irl" />
            <Field label="Porcentaje de ejemplo" value={wPct} onChange={setWPct} suffix="%" data-id="element-1019" data-mp="1335h2302iyhq" />
          </div>
          {w.ok ? <Out rows={[['Bruto', `${formatCents(w.grossCents)} USDT`], ['Comisión', `${formatCents(w.feeCents)} USDT`], ['Neto', `${formatCents(w.netCents)} USDT`, true]]} data-id="element-1020" data-mp="y6589kbu3hmx" /> : <Out error={w.error} data-id="element-1021" data-mp="2sjwqv9c55bui" />}
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-500" data-id="element-1022" data-mp="302nwlkvnnu1m">Los porcentajes iniciales (3 % y 5 %) son los ejemplos del manual, no tarifas vigentes. Escribe el porcentaje que muestre el Market.</p>
    </section>;
}